import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const HEALTH_TONE = {
  Healthy: { dot: "bg-emerald-500", text: "text-emerald-700", ring: "ring-emerald-200", label: "Healthy" },
  Degraded: { dot: "bg-amber-500", text: "text-amber-700", ring: "ring-amber-200", label: "Degraded" },
};
const SUB_TONE = {
  Operational: "text-emerald-700", Active: "text-emerald-700", Loaded: "text-amber-700",
  Degraded: "text-amber-700", Unavailable: "text-red-700",
};
const STATUS_TONE = {
  completed: "bg-emerald-50 text-emerald-700", failed: "bg-red-50 text-red-700",
  running: "bg-blue-50 text-blue-700", cancelled: "bg-slate-100 text-slate-600",
  skipped: "bg-slate-100 text-slate-600", pending: "bg-slate-100 text-slate-600",
};

function Card({ title, icon, children, testid }) {
  const Icon = Icons[icon] || Icons.Activity;
  return (
    <section data-testid={testid} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-slate-500" />
        <h2 className="text-sm font-semibold text-slate-800 font-heading">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function SubRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`text-sm font-medium ${SUB_TONE[value] || "text-slate-700"}`} data-testid={`sub-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</span>
    </div>
  );
}

function Dur({ ms }) {
  if (ms == null) return <span className="text-slate-400">—</span>;
  return <span>{ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`}</span>;
}

export default function AIOperations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get("/ai-ops/diagnostics");
      setData(res.data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const health = data?.health || {};
  const tone = HEALTH_TONE[health.overall] || HEALTH_TONE.Degraded;
  const activity = data?.recent_activity || [];
  const issues = data?.recent_issues || [];
  const reg = data?.registry || {};

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="ai-operations-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading">AI Operations</h1>
          <p className="text-sm text-slate-500 mt-1">A quick, read-only health check of the AI platform.</p>
        </div>
        <button
          data-testid="ai-ops-refresh"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm border border-slate-300 rounded-lg px-3 py-2 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <Icons.RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && (
        <div data-testid="ai-ops-error" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Diagnostics are temporarily unavailable. The AI platform continues to operate normally.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Card 1 — AI Health */}
        <Card title="AI Health" icon="HeartPulse" testid="card-ai-health">
          <div className={`flex items-center gap-3 mb-4 rounded-lg bg-slate-50 ring-1 ${tone.ring} px-4 py-3`}>
            <span className={`h-3 w-3 rounded-full ${tone.dot}`} />
            <span className={`text-base font-semibold ${tone.text}`} data-testid="health-overall">{tone.label}</span>
          </div>
          <SubRow label="Registry" value={health.registry_status || "—"} />
          <SubRow label="Observability" value={health.observability_status || "—"} />
          <SubRow label="Shadow Attribution" value={health.shadow_attribution_status || "—"} />
        </Card>

        {/* Card 4 — Workflow Registry (placed beside health for balance) */}
        <Card title="Workflow Registry" icon="Boxes" testid="card-registry">
          <SubRow label="Registry Loaded" value={reg.loaded ? "Yes" : "No"} />
          <SubRow label="Active Version" value={reg.active_version || "—"} />
          <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
            <span className="text-sm text-slate-500">Registered Workflows</span>
            <span className="text-sm font-medium text-slate-700" data-testid="registry-count">{reg.workflow_count ?? "—"}</span>
          </div>
          <div className="flex items-center justify-between py-1.5">
            <span className="text-sm text-slate-500">Last Registry Load</span>
            <span className="text-sm font-medium text-slate-700" data-testid="registry-loaded-at">{reg.loaded_at ? fmtDateTime(reg.loaded_at) : "—"}</span>
          </div>
        </Card>

        {/* Card 2 — Recent Activity */}
        <Card title="Recent Activity" icon="Activity" testid="card-recent-activity">
          {activity.length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid="activity-empty">No recent AI activity.</p>
          ) : (
            <div className="overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                    <th className="py-2 font-medium">Time</th>
                    <th className="py-2 font-medium">Workflow</th>
                    <th className="py-2 font-medium">Status</th>
                    <th className="py-2 font-medium text-right">Duration</th>
                  </tr>
                </thead>
                <tbody data-testid="activity-rows">
                  {activity.map((r, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="py-2 text-slate-500 whitespace-nowrap">{r.timestamp ? fmtDateTime(r.timestamp) : "—"}</td>
                      <td className="py-2 text-slate-700">
                        <span className="font-medium">{r.workflow_id}</span>
                        <span className="text-slate-400"> · {r.workflow_name}</span>
                      </td>
                      <td className="py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_TONE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                      </td>
                      <td className="py-2 text-right text-slate-600"><Dur ms={r.duration_ms} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Card 3 — Recent Issues */}
        <Card title="Recent Issues" icon="TriangleAlert" testid="card-recent-issues">
          {issues.length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid="issues-empty">No recent issues.</p>
          ) : (
            <ul className="space-y-2" data-testid="issues-rows">
              {issues.map((r, i) => (
                <li key={i} className="flex items-center justify-between rounded-lg bg-red-50 px-3 py-2">
                  <div className="text-sm">
                    <span className="font-medium text-red-800">{r.workflow_id}</span>
                    <span className="text-red-500"> · {r.workflow_name}</span>
                  </div>
                  <span className="text-xs text-red-500 whitespace-nowrap">{r.timestamp ? fmtDateTime(r.timestamp) : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {data?.generated_at && (
        <p className="text-xs text-slate-400 mt-6 text-right" data-testid="ai-ops-generated-at">
          Generated {fmtDateTime(data.generated_at)}
        </p>
      )}
    </div>
  );
}
